import { getSemesterName } from '../../utils/semesterUtils';
import { useDroppable } from '@dnd-kit/core';
import { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import Flag from '../../components/icons/Flag';
import CourseCard from './CourseCardInSemester';
import SearchBlock from './SearchBlock';
import { CourseOption } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';

interface SemesterCardProps {
  semester: string;
  userCourses: CourseOption[];
  isSelected: boolean;
  onSelect: () => void;
  onAddCourse: (course: CourseOption, semester: string) => void;
  onDeleteCourse: (course: CourseOption) => void;
}

export default function SemesterCard({ 
  semester, 
  userCourses, 
  isSelected, 
  onSelect,
  onAddCourse,
  onDeleteCourse
}: SemesterCardProps) {
  const coursesForSemester = userCourses.filter(
    courseOption => courseOption.userState.semester === semester && courseOption.userState.isScheduled
  );

  const totalCredits = coursesForSemester.reduce(
    (sum, courseOption) => sum + (courseOption.userState.creditsReceived ?? 0), 0
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false), isDropdownOpen);

  const { setNodeRef, isOver } = useDroppable({
    id: `semester-grid-${semester}`,
    data: { type: 'semester', semester },
  });

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSetAsDefault = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
    setIsDropdownOpen(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-lg shadow-md border-2 p-5 transition-all duration-200 hover:shadow-lg ${
        isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isSelected && (
              <Flag className="w-5 h-5 text-amber-500" />
            )}
            <h3 className="text-lg font-semibold text-gray-800">
              {getSemesterName(semester)}
            </h3>
          </div>
          <span className="text-sm text-gray-800">
            {totalCredits} credits
          </span>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button 
            onClick={handleMenuClick}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Semester options"
          >
            <svg 
              className="w-5 h-5 text-gray-600" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <circle cx="4" cy="10" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="16" cy="10" r="1.5" />
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
              <button
                onClick={handleSetAsDefault}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Set as default semester
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {coursesForSemester.map((course) => (
          <CourseCard 
            key={getCourseKey(course)} 
            courseOption={course} 
            onDeleteCourse={onDeleteCourse}
          />
        ))}
        <SearchBlock 
          userCourses={userCourses} 
          semester={semester} 
          onAddCourse={(course) => onAddCourse(course, semester)}
        />
      </div>
    </div>
  );
}
